import { FormDatePicker, FormInput, FormSelect } from "@/components";
import { Badge, Button, Input, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui";
import { cn } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { useCreateOwnershipMutation, useGetAvailableProperties, useSearchTenantCandidates } from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Info, Mail, Plus, Save, Search, Trash2, UserPlus, Users, X } from "lucide-react";
import moment from "moment";
import { ReactNode, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/Toast";
import NewOwnerDialog from "./NewOwnerDialog";

const OWNERSHIP_TYPES = [
  { label: "Individual", value: "INDIVIDUAL" },
  { label: "Joint", value: "JOINT" },
  { label: "LLC", value: "LLC" },
  { label: "Trust", value: "TRUST" },
  { label: "Corp", value: "CORP" },
];
const ENTITY_TYPES = ["LLC", "TRUST", "CORP"];
const SOURCES = [
  { value: "PROPERTY", label: "Property address" },
  { value: "OWNER_PRIMARY", label: "Primary owner address" },
  { value: "CUSTOM", label: "Custom address" },
];
const ZIP_REGEX = /^[0-9]{5}(-[0-9]{4})?$/;
const humanize = (v?: string) => (v ? v.replace(/_/g, " ") : "—");

interface SelectedOwner { userId: number; name: string; phone?: string; email?: string; primary: boolean; share: string }

const Section = ({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) => (
  <div className="flex flex-col gap-4 border-t pt-5 first:border-t-0 first:pt-0">
    <h4 className="text-xs font-semibold uppercase tracking-wide text-primary flex items-center gap-2">{icon}{title}</h4>
    {children}
  </div>
);

/** People > Property Ownership > + New Ownership: link a property to its owner(s) under one ownership ID. */
const NewOwnership = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const community = useSelector((state: RootState) => state.currentData.communityData);
  const communityId = community?.communityId && community.communityId != "all" ? String(community.communityId) : "";
  const stateList = useSelector((state: RootState) => state.currentData.stateData) || [];
  const listPath = location.pathname.replace(/\/new\/?$/, "");
  const isAdmin = location.pathname.startsWith("/admin");
  const ocpPath = listPath.replace(/\/property-ownership$/, "/owner-change-package");

  const form = useForm<any>({
    defaultValues: {
      propertyId: "", ownershipType: "INDIVIDUAL", entityName: "",
      startDate: moment().startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      correspondenceAddressSource: "PROPERTY", distribution: "ALL",
      customAddress: { addressLine1: "", addressLine2: "", pobox: "", city: "", state: "", zipCode: "", country: "USA" },
    },
  });
  const errors: any = form.formState.errors;
  const ownershipType = useWatch({ control: form.control, name: "ownershipType" });
  const source = useWatch({ control: form.control, name: "correspondenceAddressSource" });
  const isIndividual = ownershipType === "INDIVIDUAL";
  const isEntity = ENTITY_TYPES.includes(ownershipType);

  const { data: available } = useGetAvailableProperties({ communityId });
  const propertyOptions = (available?.data || []).map((p: any) => ({
    value: String(p.propertyId), label: [p.name, p.displayId].filter(Boolean).join(" · "),
  }));

  // ---- Owners: search existing people, or add a new owner
  const [owners, setOwners] = useState<SelectedOwner[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [openNewOwner, setOpenNewOwner] = useState(false);
  const { data: searchData, isFetching } = useSearchTenantCandidates({ communityId, query });
  const results = useMemo(() => {
    const byUser = new Map<number, any>();
    (searchData?.data || []).forEach((r: any) => {
      if (!byUser.has(r.userId)) byUser.set(r.userId, { ...r, relationships: [] });
      if (r.propertyUserAssociationId) byUser.get(r.userId).relationships.push(r);
    });
    return Array.from(byUser.values());
  }, [searchData]);
  const searched = query.trim().length >= 2 && !isFetching;

  const rebalance = (list: SelectedOwner[]) => {
    if (list.length === 0) return list;
    const base = Math.floor((100 / list.length) * 100) / 100;
    return list.map((o, i) => ({ ...o, share: String(i === 0 ? +(100 - base * (list.length - 1)).toFixed(2) : base) }));
  };
  const addOwner = (u: any) => {
    if (owners.some((o) => o.userId === u.userId)) return;
    if (isIndividual && owners.length >= 1) {
      toast.error("An Individual title has one owner. Choose Joint / LLC / Trust / Corp for more.");
      return;
    }
    const next = [...owners, {
      userId: u.userId, name: [u.firstName, u.lastName].filter(Boolean).join(" ") || u.userName,
      phone: u.phoneNumber, email: u.email, primary: owners.length === 0, share: "",
    }];
    setOwners(rebalance(next));
  };
  const removeOwner = (userId: number) => {
    const next = owners.filter((o) => o.userId !== userId);
    if (next.length && !next.some((o) => o.primary)) next[0] = { ...next[0], primary: true };
    setOwners(rebalance(next));
  };
  const setPrimary = (userId: number) => setOwners(owners.map((o) => ({ ...o, primary: o.userId === userId })));
  const setShare = (userId: number, share: string) => setOwners(owners.map((o) => (o.userId === userId ? { ...o, share } : o)));
  const shareTotal = owners.reduce((t, o) => t + (Number(o.share) || 0), 0);

  const { mutate: createOwnership, isPending } = useCreateOwnershipMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string" ? error.response.data.message : "Something went wrong"
      );
    },
    onSuccess: (response: any) => {
      const created = response?.data?.data;
      toast.success(`Ownership PO-${created?.ownershipId} created Successfully`);
      ["ownership-grid", "ownership-summary", "available-properties", "owner-master"].forEach((k) =>
        queryClient.invalidateQueries({ queryKey: [k] }));
      navigate(created?.ownershipId ? `${listPath}/${created.ownershipId}` : listPath);
    },
  });

  const onSave = form.handleSubmit((v: any) => {
    let ok = true;
    const fail = (name: string, message: string) => { form.setError(name, { message }); ok = false; };
    if (!v.propertyId) fail("propertyId", "Please select the property.");
    if (!v.startDate) fail("startDate", "Please select the start date.");
    if (isEntity && !v.entityName?.trim()) fail("entityName", "Please enter the entity name.");
    if (v.correspondenceAddressSource === "CUSTOM") {
      const a = v.customAddress || {};
      if (!a.addressLine1?.trim()) fail("customAddress.addressLine1", "Please enter Address Line 1.");
      if (!a.city?.trim()) fail("customAddress.city", "Please enter City.");
      if (!a.state) fail("customAddress.state", "Please select State.");
      if (!a.zipCode?.trim()) fail("customAddress.zipCode", "Please enter ZIP Code.");
      else if (!ZIP_REGEX.test(a.zipCode.trim())) fail("customAddress.zipCode", "Enter ZIP code as XXXXX or XXXXX-XXXX.");
    }
    if (owners.length === 0) { toast.error("Add at least one owner."); ok = false; }
    if (isIndividual && owners.length > 1) { toast.error("An Individual title has exactly one owner."); ok = false; }
    if (!isIndividual && owners.length && Math.abs(shareTotal - 100) > 0.001) {
      toast.error(`Owner shares must total 100% (currently ${shareTotal}%).`);
      ok = false;
    }
    if (!ok) return;

    const startDate = moment(v.startDate).format("YYYY-MM-DDT00:00:00");
    const a = v.customAddress || {};
    createOwnership({
      communityId,
      data: {
        propertyId: Number(v.propertyId),
        ownershipType: v.ownershipType,
        entityName: isEntity ? v.entityName.trim() : null,
        propertyUserAssociations: owners.map((o) => ({
          userId: o.userId, associationType: "OWNER", primaryUser: o.primary, startDate,
          sharePercentage: isIndividual ? null : Number(o.share),
        })),
        correspondenceAddressSource: v.correspondenceAddressSource,
        customAddress: v.correspondenceAddressSource === "CUSTOM" ? {
          addressLine1: a.addressLine1.trim(), addressLine2: a.addressLine2?.trim() || null, pobox: a.pobox?.trim() || null,
          city: a.city.trim(), state: stateList.find((s: any) => s.code == a.state)?.name || a.state,
          zipCode: a.zipCode.trim(), country: "USA",
        } : null,
        multiCorrespondence: v.distribution === "ALL",
      },
    });
  });

  return (
    <FormProvider {...form}>
      <div className="p-4 md:p-6 flex flex-col gap-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" size="icon" onClick={() => navigate(listPath)} aria-label="Back"><ArrowLeft className="w-4 h-4" /></Button>
            <div>
              <h2 className="text-lg md:text-xl font-semibold">New Ownership</h2>
              <p className="text-sm text-gray-500">Link a property to its owner(s) under one ownership ID</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => navigate(listPath)} disabled={isPending}><X className="w-4 h-4" /> Cancel</Button>
            <Button type="button" onClick={onSave} disabled={isPending || !communityId}><Save className="w-4 h-4" /> Save Ownership</Button>
          </div>
        </div>

        <div className="bg-white rounded-xl border-t-4 border-primary shadow-sm p-4 md:p-6 flex flex-col gap-5">
          <Section title="Title" icon={<Info className="w-4 h-4" />}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Community</p>
                {communityId
                  ? <p className="text-sm font-semibold mt-1">{community?.name}</p>
                  : <p className="text-sm text-red-600 mt-1">Select a community on the Property Ownership page.</p>}
              </div>
              <FormSelect name="propertyId" control={form.control} label="Property" isRequired placeholder="Please select"
                options={propertyOptions} error={errors.propertyId?.message} />
              <FormSelect name="ownershipType" control={form.control} label="Ownership Type" isRequired options={OWNERSHIP_TYPES} />
              <FormDatePicker name="startDate" control={form.control} label="Start Date" isRequired error={errors.startDate?.message} />
              {isEntity ? (
                <FormInput name="entityName" control={form.control} label={`${humanize(ownershipType)} Name`} isRequired
                  placeholder="Legal entity / trust name" error={errors.entityName?.message} />
              ) : <></>}
            </div>
            <p className="text-xs bg-amber-50 border border-amber-200 rounded-md p-2 text-gray-700">
              Only properties <b>without an owner</b> are listed. To change the ownership of a property that already has a
              title, use the{" "}
              {isAdmin ? (
                <button type="button" className="text-primary underline" onClick={() => navigate(ocpPath)}>
                  Ownership Change Package (OCP)
                </button>
              ) : <b>Ownership Change Package (OCP)</b>}{" "}
              module.
            </p>
            {propertyOptions.length === 0 && communityId ? (
              <p className="text-xs text-gray-500">Every property in this community already has an owner.</p>
            ) : <></>}
          </Section>

          <Section title="Owners" icon={<Users className="w-4 h-4" />}>
            {!isIndividual ? (
              <p className="text-xs bg-blue-50 border border-blue-100 rounded-md p-2 text-gray-700">
                Only the <b>primary owner</b> gets portal access. Other owners are created <b>inactive</b> (no login, no credentials
                email) unless they're already active on another title. Activate them later from Users.
              </p>
            ) : <></>}
            <div className="flex gap-2">
              <Input className="flex-1 bg-white h-[40px]" placeholder="Search owner by name, phone number or email — press Enter"
                value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setQuery(searchInput.trim()); } }}
                startIcon={<Search className="w-4 h-4" />} />
              <Button type="button" variant="outline" disabled={searchInput.trim().length < 2} onClick={() => setQuery(searchInput.trim())}>Search</Button>
            </div>

            {isFetching ? <p className="text-sm text-gray-500">Searching…</p> : <></>}
            {searched && results.length === 0 ? (
              <div className="flex flex-wrap items-center gap-3 border rounded-lg p-3 bg-gray-50">
                <p className="text-sm text-gray-600">No match found for "{query}".</p>
                <Button type="button" variant="outline" size="sm" disabled={!communityId} onClick={() => setOpenNewOwner(true)}>
                  <UserPlus className="w-4 h-4" /> Add New Owner
                </Button>
              </div>
            ) : <></>}
            {searched && results.length > 0 ? (
              <div className="border rounded-lg max-h-[260px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {["Name", "Phone Number", "Email", "Status", "Relationships", ""].map((h) => (
                        <TableHead key={h} className="font-semibold text-primary whitespace-nowrap">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map((u) => (
                      <TableRow key={u.userId}>
                        <TableCell className="font-medium">{[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}</TableCell>
                        <TableCell>{u.phoneNumber || "—"}</TableCell>
                        <TableCell>{u.email || "—"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={cn("rounded-md px-2 py-0.5 text-xs",
                            u.userActive === false ? "border-gray-200 bg-gray-100 text-gray-600" : "border-green-200 bg-green-50 text-green-700")}>
                            {u.userActive === false ? "Inactive" : "Active"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-gray-600">
                          {u.relationships.length
                            ? u.relationships.slice(0, 3).map((r: any) => `${humanize(r.relationship)} · ${r.propertyName} (${r.communityName})`).join("; ")
                              + (u.relationships.length > 3 ? ` +${u.relationships.length - 3} more` : "")
                            : "No relationships"}
                        </TableCell>
                        <TableCell className="text-right">
                          {owners.some((o) => o.userId === u.userId)
                            ? <span className="text-xs text-gray-500">Added</span>
                            : <Button type="button" size="sm" onClick={() => addOwner(u)}><Plus className="w-4 h-4" /> Add</Button>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : <></>}

            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {["Owner", "Phone Number", "Email", "Primary", ...(isIndividual ? [] : ["Share %"]), "Portal Access", ""].map((h) => (
                      <TableHead key={h} className="font-semibold text-primary whitespace-nowrap">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {owners.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-gray-500 h-14">No owners added yet.</TableCell></TableRow>
                  ) : owners.map((o) => (
                    <TableRow key={o.userId}>
                      <TableCell className="font-medium">{o.name}</TableCell>
                      <TableCell>{o.phone || "—"}</TableCell>
                      <TableCell>{o.email || "—"}</TableCell>
                      <TableCell>
                        <input type="radio" name="primaryOwner" checked={o.primary} onChange={() => setPrimary(o.userId)} aria-label={`Primary: ${o.name}`} />
                      </TableCell>
                      {isIndividual ? <></> : (
                        <TableCell className="w-[120px]">
                          <Input type="number" min={0} max={100} value={o.share} onChange={(e) => setShare(o.userId, e.target.value)} className="h-[34px]" />
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge variant="outline" className={cn("rounded-md px-2 py-0.5 text-xs",
                          o.primary || isIndividual ? "border-green-200 bg-green-50 text-green-700" : "border-gray-200 bg-gray-100 text-gray-600")}>
                          {o.primary || isIndividual ? "Active" : "Inactive unless active elsewhere"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeOwner(o.userId)} aria-label={`Remove ${o.name}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {!isIndividual && owners.length ? (
              <p className={cn("text-xs", Math.abs(shareTotal - 100) > 0.001 ? "text-red-600" : "text-gray-500")}>
                Set each owner's % share — shares total {shareTotal}% (must be 100%).
              </p>
            ) : <></>}
          </Section>

          <Section title="Correspondence" icon={<Mail className="w-4 h-4" />}>
            <div className="flex flex-wrap gap-4">
              {SOURCES.map((s) => (
                <label key={s.value} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="correspondenceSource" checked={source === s.value}
                    onChange={() => form.setValue("correspondenceAddressSource", s.value)} />
                  {s.label}
                </label>
              ))}
            </div>
            {source === "CUSTOM" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <FormInput name="customAddress.addressLine1" control={form.control} label="Address Line 1" isRequired placeholder="Please enter" error={errors.customAddress?.addressLine1?.message} />
                <FormInput name="customAddress.addressLine2" control={form.control} label="Address Line 2" placeholder="Please enter" />
                <FormInput name="customAddress.pobox" control={form.control} label="PO Box" placeholder="Please enter" />
                <FormInput name="customAddress.city" control={form.control} label="City" isRequired placeholder="Please enter" variant="char" error={errors.customAddress?.city?.message} />
                <FormSelect name="customAddress.state" control={form.control} label="State" isRequired placeholder="Please select"
                  options={stateList.map((s: any) => ({ label: s.name, value: s.code }))} error={errors.customAddress?.state?.message} />
                <FormInput name="customAddress.zipCode" control={form.control} label="ZIP Code" isRequired placeholder="XXXXX or XXXXX-XXXX"
                  maxLength={10} variant="integerSpeChar" error={errors.customAddress?.zipCode?.message} />
                <FormInput name="customAddress.country" control={form.control} label="Country" disabled />
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                {source === "PROPERTY" ? "Mail goes to the property's address." : "Mail goes to the primary owner's contact address."}
              </p>
            )}
            <div className="w-full sm:w-72">
              <FormSelect name="distribution" control={form.control} label="Distribution" options={[
                { label: "All owners on this title", value: "ALL" },
                { label: "Primary contact only", value: "PRIMARY" },
              ]} />
            </div>
          </Section>
        </div>
      </div>

      {openNewOwner ? (
        <NewOwnerDialog open={openNewOwner} setOpen={setOpenNewOwner} communityId={communityId} onCreated={addOwner} />
      ) : <></>}
    </FormProvider>
  );
};

export default NewOwnership;
